import { 
    useState, 
    useEffect, 
    useReducer,
    useRef,
    useCallback,
    useContext,
    createContext
} from 'react';

import { 
    motion,
    useInView 
} from "framer-motion"


import { 
    Dots,
    ActionContext,
    SettingsContext
} from '../../../src/view.jsx';



export function Controls() {
    const { 
        processing
    } = useContext( ActionContext )


    return (
        <div className={ `wp-block-cwc-slider__controls${processing ? ' is-processing' : ''}` }>
            <Control 
                side="prev"
            /> 

            <Control 
                side="next"
            /> 
        </div>
    )
}


/**
 * Display slider controls
 * @prop {string} side - Control side (prev|next)
 * @prop {int} currentIndex - 
 * @prop {boolean} autoplay - Autoplay slider
 * @prop {func} setSlide - Preview or next slide action
 */
function Control( {
    side
} ) {
    const { 
        stopOnHover,
        autoplay,
        hasMobile,
        controlsType
    } = useContext( SettingsContext )

    const { 
        currentIndex: [
            prev,
            current,
            next
        ],
        switchSlide,
        setPlay
    } = useContext( ActionContext )

    const variants = {
        hidden: (side) => {
            return {
                x: 0,
                y: '-50%',
                opacity: 0
            };
        },
        visible: (side) => {
            return {
                x: controlsType == 'outside' ? 0 : side == 'prev' ? 10 : -10,
                y: '-50%',
                opacity: controlsType == 'outside' ? 1 : 0.4,
                transition: {
                    delay: 1,
                    x: { 
                        type: 'spring', 
                        stiffness: 400, 
                        damping: 20
                    },
                    opacity: {
                        duration: 0.2
                    }
                }
            };
        }
    }

    const mobileVariants = {
        hidden: (side) => {
            return {
                //x: 0,
                y: '-50%',
                opacity: 0
            };
        },
        visible: (side) => {
            return {
                //x: controlsType == 'outside' ? null : [0, 0, 0, side == 'prev' ? -50 : 50],
                y: '-50%',
                opacity: controlsType == 'outside' ? 1 : [0, 0.8, 0.8, 0],
                transition: {
                    delay: 1,
                    times: controlsType == 'outside' ? null : [0, 0.1, 0.9, 1],
                    duration: controlsType == 'outside' ? 0.3 : 3,
                    //repeat: controlsType == 'outside' ? null : Infinity,
                    //repeatDelay: 10
                }
            };
        }
    }


    return (
        <motion.span 
            className={ `wp-block-cwc-slider__control ${side}` } 
            custom={ side }
            variants={ hasMobile ? mobileVariants : variants  }
            initial="hidden"
            animate="visible"
            whileHover={ !hasMobile ? {
                opacity: 1,
                transition: {
                    delay: 0,
                    duration: 0.5
                }
            } : null }
            style={ {
                pointerEvents: hasMobile ? 'none' : 'all'
            } }
            onClick={ () => hasMobile ? null : switchSlide( side == 'prev' ? prev : next, side == 'prev' ? 'left' : 'right' ) }
            onPointerEnter={ () => autoplay && !hasMobile && stopOnHover ? setPlay( false ) : null } 
            onPointerLeave={ () => autoplay && !hasMobile && stopOnHover ? setPlay( true ) : null }
        >
            { side == 'prev' ? 
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path fill="currentColor" d="M29.7,6.5L16.2,20l13.5,13.5l-3,3L10.3,20L26.8,3.5L29.7,6.5z"></path></svg>
            : 
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path fill="currentColor" d="M9.9,6.9l3.6-3.5L30.1,20L13.5,36.6l-3.6-3.5L23.1,20L9.9,6.9z"></path></svg>
            } 
        </motion.span>
    )
}



/**
 * Display app
 * @prop {array} items - BlockSlider items array nodes
 * @prop {node} itemsThumbnails - BlockSlider thumbnails node
 * @prop {array} itemStyles - Item styles array nodes
 * @prop {node} staticContent - Item content node
 * @prop {string} minHeight - Min height
 * @prop {boolean} autoplay - Autoplay slider
 * @prop {integer} rate - Transition rate
 * @prop {integer} delay - Transition delay
 * @prop {boolean} controls - Display next prev controls
 * @prop {boolean} thumbnails - Display slides thumbnails
 */
export default function WpBlockCwcSlider( {
    children,
    delay,
    effect,
    stopOnHover,
    controlsType,
    hasDots,
    rate,
    autoplay,
    type,
    slidesCount,
    defaultIndex,
    mobileQuery = 600,
    hasMobile: defaultHasMobile,
    ...otherProps
} ) {
    const wrapperRef = useRef()

    const isInView = useInView( wrapperRef )

    const processingDelay = autoplay ? parseInt( delay ) + parseInt( rate ) : parseInt( delay )
    const maxIndex = slidesCount - 1

    const planingAutoplay = useRef( null ) // autoplay number

    const [ hasMobile, setHasMobile ] = useState( defaultHasMobile )

    const [ processing, setProcessing ] = useState( false )
    const [ play, setPlay ] = useState( autoplay )
    const [ direction, setDirection ] = useState( 'right' )
    const [ currentIndex, setCurrentIndex ] = useReducer( (state, action) => {
        return [
            action === 0 ? maxIndex : action - 1,
            action,
            action === maxIndex ? 0 : action + 1
        ]
    }, [
        defaultIndex === 0 ? maxIndex : defaultIndex - 1,
        defaultIndex,
        defaultIndex === maxIndex ? 0 : defaultIndex + 1
    ] )


    /**
     * Switches to the next or previous slide.
     * @param {int} index - New slide index (default: 0)
     * @param {string} direction - 
     */
    const switchSlide = (index = 0, direction = null) => {
        if ( !processing ) {
            const [ prev, current, next ] = currentIndex

            setProcessing( true )
            setDirection( direction ? direction : index < current ? 'left' : 'right' )
            setCurrentIndex( index )
        }
    }


    const togglePlaningNext = action => {
        if ( action ) {
            const [ prev, current, next ] = currentIndex

            planingAutoplay.current = setTimeout( () => {
                switchSlide( next, 'right' )
            }, processingDelay )
        } else {
            clearTimeout( planingAutoplay.current )
            planingAutoplay.current = null
        }
    }


    useEffect( () => {
        if ( autoplay ) {
            togglePlaningNext( play )
        }
    }, [play, processing] )


    useEffect( () => {
        if ( autoplay ) {
            setPlay( isInView )
        }
    }, [isInView] )


    return (
        <SettingsContext.Provider value={ { 
            type,
            effect,
            rate,
            direction,
            autoplay,
            slidesCount,
            stopOnHover,
            hasMobile,
            controlsType
        } }>
            <ActionContext.Provider value={ { 
                currentIndex, 
                switchSlide,
                processing, 
                setProcessing,
                setPlay
            } }>
                <div 
                    ref={ wrapperRef }
                    className="wp-block-cwc-slider__wrapper"
                    onPointerEnter={ () => autoplay && !hasMobile && stopOnHover ? setPlay( false ) : null } 
                    onPointerLeave={ () => autoplay && !hasMobile && stopOnHover ? setPlay( true ) : null }
                >
                    { children }
                </div>

                { controlsType !== '' &&
                    <Controls />
                }
            </ActionContext.Provider> 
        </SettingsContext.Provider>
    )
}